export const TownsquareInteractionName = {
	// Users can view directories: https://tome.prod.atl-paas.net/capability/7bfb7463-655a-4475-b93a-790a7cefa6d4
	ProjectDirectoryViewed: 'projectDirectoryViewed',
	GoalDirectoryViewed: 'goalDirectoryViewed',
	TopicDirectoryViewed: 'topicDirectoryViewed',
	TopicPageViewed: 'topicPageViewed',
	ProjectAboutPageViewed: 'projectAboutPageViewed',
	ProjectUpdatesPageViewed: 'projectUpdatesPageViewed',
	ProjectLearningsPageViewed: 'projectLearningsPageViewed',
	ReadingModeViewed: 'readingModeViewed',
	TopicsHelpAndKnowledgePageViewed: 'topicsHelpAndKnowledgePageViewed',
	TopicsUpdatesPageViewed: 'topicsUpdatesPageViewed',
	PersonalSettingsPageViewed: 'personalSettingsPageViewed',
	WorkspaceSettingsPageViewed: 'workspaceSettingsPageViewed',
	FieldSettingsPageViewed: 'fieldSettingsPageViewed',
	TeamsSettingsPageViewed: 'teamsSettingsPageViewed',

	// No workspace
	NoWorkspaceTeamsDirectoryViewed: 'noWorkspaceTeamsDirectoryViewed',
	NoWorkspaceProjectsDirectoryViewed: 'noWorkspaceProjectsDirectoryViewed',
	NoWorkspaceGoalsDirectoryViewed: 'noWorkspaceGoalsDirectoryViewed',
	NoWorkspaceStatusUpdatesPageViewed: 'noWorkspaceStatusUpdatesPageViewed',

	// Share modals
	ProjectShareModalViewed: 'projectShareModalViewed',
	GoalShareModalViewed: 'goalShareModalViewed',

	TagUpdatesFeedViewed: 'tagUpdatesFeedViewed',
	TeamUpdatesFeedViewed: 'teamUpdatesFeedViewed',
	SavedViewUpdatesViewed: 'savedViewUpdatesViewed',
	ProjectUpdatesFeedViewed: 'projectUpdatesFeedViewed',
	GoalUpdatesFeedViewed: 'goalUpdatesFeedViewed',

	// Focus area sidebar
	// Dashboards: https://tome.prod.atl-paas.net/capability/d3b83c3d-5f28-4552-99b0-158d8fe7abc4
	FocusAreaUnlinked: 'townsquare-unlink-focus-area',
	FocusAreaSidebarViewed: 'focusAreaSidebarViewed', // pre-existing in townsquare

	// Goal page tabs
	// Dashboards: https://tome.prod.atl-paas.net/capability/ecec3a20-a466-432b-bb6f-9ebdcbf87560
	GoalAboutTabViewed: 'goal-about-tab-viewed',
	GoalUpdatesTabViewed: 'goal-updates-tab-viewed',
	GoalJiraTabViewed: 'goal-jira-tab-viewed',
	GoalJiraAlignTabViewed: 'goal-jiraAlign-tab-viewed',
	GoalProjectsTabViewed: 'goal-projects-tab-viewed',
	GoalLearningsTabViewed: 'goal-learnings-tab-viewed',
	GoalRisksTabViewed: 'goal-risks-tab-viewed',
	GoalDecisionsTabViewed: 'goal-decisions-tab-viewed',

	// Project page tabs
	// Dashboards: https://tome.prod.atl-paas.net/capability/ecec3a20-a466-432b-bb6f-9ebdcbf87560
	ProjectAboutTabViewed: 'project-about-tab-viewed',
	ProjectUpdatesTabViewed: 'project-updates-tab-viewed',
	ProjectLearningsTabViewed: 'project-learnings-tab-viewed',
	ProjectRisksTabViewed: 'project-risks-tab-viewed',
	ProjectDecisionsTabViewed: 'project-decisions-tab-viewed',

	// Comments
	// https://tome.prod.atl-paas.net/capability/8edd3fa6-02ff-424f-968f-33a618bf53b6
	GoalCommentsViewed: 'goalCommentsViewed',
	ProjectCommentsViewed: 'projectCommentsViewed',
	GoalUpdateCommentsViewed: 'goalUpdateCommentsViewed',
	ProjectUpdatesCommentsViewed: 'projectUpdateCommentsViewed',

	//Starred feed
	StarredFeedViewed: 'starredFeedViewed',

	// Home / page-level segmented regions
	// Dashboards: https://tome.prod.atl-paas.net/capability/d3b83c3d-5f28-4552-99b0-158d8fe7abc4
	WhereYouWorkViewed: 'whereYouWorkViewed', // pre-existing in townsquare
	YourAppsSectionViewed: 'yourAppsSectionViewed', // pre-existing in townsquare
	ThreadsViewed: 'threadsViewed', // pre-existing in townsquare

	// Metric targets
	CreateMetricTarget: 'townsquare-create-metric-target',
} as const;

export const TownsquareLoadHoldName = {
	CreateMetricTargetLoading: 'townsquare-create-metric-target-loading',
} as const;

export const GoalTabSegmentName: {
	readonly about: 'goal-about-tab-viewed';
	readonly updates: 'goal-updates-tab-viewed';
	readonly jira: 'goal-jira-tab-viewed';
	readonly jiraAlign: 'goal-jiraAlign-tab-viewed';
	readonly projects: 'goal-projects-tab-viewed';
	readonly learnings: 'goal-learnings-tab-viewed';
	readonly risks: 'goal-risks-tab-viewed';
	readonly decisions: 'goal-decisions-tab-viewed';
} = {
	about: TownsquareInteractionName.GoalAboutTabViewed,
	updates: TownsquareInteractionName.GoalUpdatesTabViewed,
	jira: TownsquareInteractionName.GoalJiraTabViewed,
	jiraAlign: TownsquareInteractionName.GoalJiraAlignTabViewed,
	projects: TownsquareInteractionName.GoalProjectsTabViewed,
	learnings: TownsquareInteractionName.GoalLearningsTabViewed,
	risks: TownsquareInteractionName.GoalRisksTabViewed,
	decisions: TownsquareInteractionName.GoalDecisionsTabViewed,
} as const;

/** Union of valid goal tab names that have a registered UFO segment. */
export type GoalTabName = keyof typeof GoalTabSegmentName;

/** Type guard narrowing an arbitrary string to a known `GoalTabName`. */
export const isGoalTabName = (tabName: string): tabName is GoalTabName =>
	tabName in GoalTabSegmentName;

export const ProjectTabSegmentName: {
	readonly about: 'project-about-tab-viewed';
	readonly updates: 'project-updates-tab-viewed';
	readonly learnings: 'project-learnings-tab-viewed';
	readonly risks: 'project-risks-tab-viewed';
	readonly decisions: 'project-decisions-tab-viewed';
} = {
	about: TownsquareInteractionName.ProjectAboutTabViewed,
	updates: TownsquareInteractionName.ProjectUpdatesTabViewed,
	learnings: TownsquareInteractionName.ProjectLearningsTabViewed,
	risks: TownsquareInteractionName.ProjectRisksTabViewed,
	decisions: TownsquareInteractionName.ProjectDecisionsTabViewed,
} as const;

/** Union of valid project tab names that have a registered UFO segment. */
export type ProjectTabName = keyof typeof ProjectTabSegmentName;

/** Type guard narrowing an arbitrary string to a known `ProjectTabName`. */
export const isProjectTabName = (tabName: string): tabName is ProjectTabName =>
	tabName in ProjectTabSegmentName;

export const CommentsSegmentName: {
	readonly goal: 'goalCommentsViewed';
	readonly 'goal-update': 'goalUpdateCommentsViewed';
	readonly project: 'projectCommentsViewed';
	readonly 'project-update': 'projectUpdateCommentsViewed';
} = {
	goal: TownsquareInteractionName.GoalCommentsViewed,
	'goal-update': TownsquareInteractionName.GoalUpdateCommentsViewed,
	project: TownsquareInteractionName.ProjectCommentsViewed,
	'project-update': TownsquareInteractionName.ProjectUpdatesCommentsViewed,
} as const;
